/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableProps } from "../../../interfaces/table";
import useModals from "../../../hooks/useModals";
import DeleteModal from "../../ui/modals/DeleteModal";
import ElementsDetails from "../elements/ElementsDetails";
import { useSocialStore } from "../../../stores/social";
import { useUserContext } from "../../../contexts/userContext";
import { useTranslation } from "react-i18next";
import {
  Badge,
  CheckIcon,
  DesktopTable,
  IconButton,
  MicroLabel,
  MobileCardFooter,
  MobileCardList,
  MobileField,
  MobileFieldGrid,
  PencilIcon,
  TableEmptyState,
  TableFrame,
  Td,
  Th,
  TrashIcon,
  cardClasses,
  emptyStateIcon,
  renderCellValue,
  rowClasses,
} from "../../ui/tables/tableUi";

/** Résout une valeur API du type { EN, FR } selon la langue i18n. */
const pickByLanguage = (
  value: unknown,
  lang: string,
): string | undefined => {
  if (value == null || typeof value !== "object") return undefined;
  const o = value as Record<string, unknown>;
  if ("EN" in o && "FR" in o) {
    return String(lang.toLowerCase().startsWith("en") ? o.EN : o.FR);
  }
  if ("en" in o && "fr" in o) {
    return String(lang.toLowerCase().startsWith("en") ? o.en : o.fr);
  }
  return undefined;
};

const TableElements = ({ columns, data }: TableProps) => {
  const { t, i18n } = useTranslation();
  const { modals, openModals, openModal, closeModal } = useModals();
  const { customer } = useSocialStore();
  const { typeRole } = useUserContext();

  const rows = data ?? [];
  const [primaryColumn, ...secondaryColumns] = columns;
  const lang = i18n.resolvedLanguage ?? i18n.language;
  const canEdit =
    typeRole === "Company" ||
    (typeRole === "Customer" && customer?.sc_Completed === false);

  const renderCell = (col: { accessor: string }, row: any) => {
    const cell = row[col.accessor];

    if (col.accessor === "morning" || col.accessor === "afternoon") {
      return cell === true ? (
        <Badge tone="emerald" icon={<CheckIcon />}>
          {t("Oui")}
        </Badge>
      ) : (
        <Badge tone="slate">{t("Non")}</Badge>
      );
    }

    const localized = pickByLanguage(cell, lang);
    if (localized !== undefined) {
      return localized;
    }

    if (cell !== null && typeof cell === "object") {
      return null;
    }

    return renderCellValue(cell);
  };

  const renderRowActions = (row: any) => (
    <div className="flex items-center justify-end gap-1.5">
      <IconButton
        label={t("Modifier")}
        tone="indigo"
        onClick={(e) => {
          e.stopPropagation();
          openModal(
            "elementsDetails",
            <ElementsDetails
              closeModal={() => closeModal("elementsDetails")}
              createOrEdit="edit"
              id={row.id}
            />,
          );
        }}
      >
        <PencilIcon />
      </IconButton>
      <IconButton
        label={t("Supprimer")}
        tone="danger"
        onClick={(e) => {
          e.stopPropagation();
          openModal(
            "deleteModal",
            <DeleteModal
              closeModal={() => closeModal("deleteModal")}
              id={row.id}
            />,
          );
        }}
      >
        <TrashIcon />
      </IconButton>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}

      <TableFrame>
        {rows.length === 0 ? (
          <TableEmptyState
            title={t("Aucune donnée disponible")}
            description={t("Les données apparaîtront ici une fois ajoutées")}
            icon={emptyStateIcon(
              "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
            )}
          />
        ) : (
          <>
            <DesktopTable>
              <thead className="sticky top-0 z-10">
                <tr>
                  {columns.map((col, index) => (
                    <Th key={index}>{col.header}</Th>
                  ))}
                  {canEdit ? (
                    <Th align="right" className="w-px">
                      {t("Actions")}
                    </Th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="font-Nunito">
                {rows.map((row, rowIndex) => (
                  <tr
                    key={row.id ?? rowIndex}
                    className={rowClasses({ clickable: false })}
                  >
                    {columns.map((col, colIndex) => (
                      <Td
                        key={colIndex}
                        className={
                          colIndex === 0
                            ? "font-semibold text-slate-900"
                            : "text-slate-600"
                        }
                      >
                        {renderCell(col, row)}
                      </Td>
                    ))}
                    {canEdit ? <Td>{renderRowActions(row)}</Td> : null}
                  </tr>
                ))}
              </tbody>
            </DesktopTable>

            <MobileCardList>
              {rows.map((row, rowIndex) => (
                <li key={row.id ?? rowIndex}>
                  <div className={cardClasses({})}>
                    <div className="min-w-0">
                      <MicroLabel>{primaryColumn?.header}</MicroLabel>
                      <div className="mt-0.5 text-[15px] leading-snug font-bold wrap-break-word text-slate-900">
                        {primaryColumn ? renderCell(primaryColumn, row) : null}
                      </div>
                    </div>

                    {secondaryColumns.length > 0 && (
                      <MobileFieldGrid>
                        {secondaryColumns.map((col) => (
                          <MobileField
                            key={col.accessor}
                            label={col.header}
                            value={renderCell(col, row)}
                          />
                        ))}
                      </MobileFieldGrid>
                    )}

                    {canEdit ? (
                      <MobileCardFooter>{renderRowActions(row)}</MobileCardFooter>
                    ) : null}
                  </div>
                </li>
              ))}
            </MobileCardList>
          </>
        )}
      </TableFrame>
    </div>
  );
};

export default TableElements;
